import { Check, Command, Unlink, X } from "lucide-react";
import { useMemo } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";

import { ACCESS_FILTER_ALL } from "../constants/access.constants";
import type { PoliciesBucket, PoliciesBucketCounts } from "../hooks/usePoliciesCatalog";
import { AccessKpiStrip, type AccessKpiTile } from "./AccessKpiStrip";

export interface PoliciesKpiStripProps {
  /** `null` while the catalog loads — every tile then reads `—` instead of asserting four zeros. */
  counts: PoliciesBucketCounts | null;
  /**
   * The roles catalog could not be read, so "used by N roles" is unknown for every policy. Only the "unused"
   * tile depends on it; reporting it as a number would state that no role links anything.
   */
  usageUnknown: boolean;
  value: PoliciesBucket;
  onChange: (next: PoliciesBucket) => void;
}

/**
 * Allow and deny get their own tiles because the model is deny-first: a single deny policy overrides every
 * allow that would otherwise apply, so "how many of these take access away" is not a category count but the
 * shape of the whole catalog. Reading a list top to bottom does not answer it.
 *
 * "Sin usar" is the fourth because it is the only actionable one: a policy no role links affects nobody, and
 * it is also the only kind that can be deleted — `DELETE` answers 409 for all the others.
 */
export function PoliciesKpiStrip({ counts, usageUnknown, value, onChange }: PoliciesKpiStripProps) {
  const { t } = useTranslations("settings");

  const tiles = useMemo<AccessKpiTile<PoliciesBucket>[]>(
    () => [
      {
        key: ACCESS_FILTER_ALL,
        label: t("access_policies_kpi_total"),
        value: counts?.all ?? null,
        icon: Command,
        accent: "neutral",
      },
      {
        key: "allow",
        label: t("access_policies_kpi_allow"),
        value: counts?.allow ?? null,
        icon: Check,
        accent: "primary",
      },
      {
        key: "deny",
        label: t("access_policies_kpi_deny"),
        value: counts?.deny ?? null,
        // The meta only appears when there is something to explain; on zero it would caption nothing.
        meta: counts && counts.deny > 0 ? t("access_policies_kpi_deny_meta") : undefined,
        icon: X,
        accent: "danger",
      },
      {
        key: "unused",
        label: t("access_policies_kpi_unused"),
        value: usageUnknown ? null : (counts?.unused ?? null),
        meta: usageUnknown
          ? t("access_policies_usage_unknown_meta")
          : counts && counts.unused > 0
            ? t("access_policies_kpi_unused_meta")
            : undefined,
        icon: Unlink,
        accent: "warning",
      },
    ],
    [t, counts, usageUnknown],
  );

  return (
    <AccessKpiStrip
      ariaLabel={t("access_policies_kpi_group_label")}
      tiles={tiles}
      value={value}
      onChange={onChange}
    />
  );
}
